'use strict'
import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import {
  makeStyles,
  Paper,
  Box,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import DirectionsIcon from '@material-ui/icons/Directions'
import { LoadScript, GoogleMap, Marker, Polyline } from '@react-google-maps/api'
import { getCrewColor } from './crewColors'

const ROUTE_MAP_STYLE = { width: '100%', height: 360 }
const DEFAULT_CENTER = { lat: 33.45, lng: -112.07 }

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  },
  header: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    marginBottom: theme.spacing(1.5)
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  mapContainer: {
    marginBottom: theme.spacing(2),
    borderRadius: 4,
    overflow: 'hidden'
  },
  table: {
    minWidth: 560
  },
  cell: {
    padding: '6px 10px'
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    padding: '8px 10px'
  },
  orderBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.75rem'
  },
  addressText: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary
  }
}))

export default function CrewDayRouteDetail ({
  url,
  selectedScenarioId,
  crewId,
  crewName,
  date,
  crewIds,
  onSnackbar
}) {
  const classes = useStyles()

  const [routeData, setRouteData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [mapRef, setMapRef] = useState(null)

  const crewColor = getCrewColor(crewId, crewIds)

  const fetchRoute = useCallback(async () => {
    if (!crewId || !date) return
    setLoading(true)
    try {
      const basePath = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${crewId}/route`
        : `${url}/api/hmi/territory/crews/${crewId}/route`
      const response = await axios.get(basePath, { params: { date } })
      setRouteData(response.data || null)
    } catch (err) {
      console.error('Error loading crew route:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to load route', 'error')
      setRouteData(null)
    } finally {
      setLoading(false)
    }
  }, [url, selectedScenarioId, crewId, date, onSnackbar])

  useEffect(() => {
    fetchRoute()
  }, [fetchRoute])

  // Fit map bounds to route
  useEffect(() => {
    if (!mapRef || !routeData?.route?.geometry?.coordinates?.length) return
    if (!window.google?.maps) return
    const bounds = new window.google.maps.LatLngBounds()
    routeData.route.geometry.coordinates.forEach(([lng, lat]) => {
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        bounds.extend({ lat, lng })
      }
    })
    if (!bounds.isEmpty()) {
      mapRef.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 })
    }
  }, [mapRef, routeData])

  const stops = routeData?.stops || []
  const meta = routeData?.meta || {}
  const routeCoords = routeData?.route?.geometry?.coordinates || []
  const polylinePath = routeCoords.map(([lng, lat]) => ({ lat, lng }))
  const totalServiceMin = stops.reduce((sum, s) => sum + (s.serviceMinutes || 0), 0)
  const totalDriveMin = stops.reduce((sum, s) => sum + (s.driveTimeMinutes || 0), 0)
  const totalMiles = stops.reduce((sum, s) => sum + (s.driveMiles || 0), 0)

  if (loading) {
    return (
      <Paper variant="outlined" className={classes.root}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading route for {crewName || crewId}...</Typography>
        </Box>
      </Paper>
    )
  }

  if (!routeData || !stops.length) {
    return (
      <Paper variant="outlined" className={classes.root}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="textSecondary">
            No route data for {crewName || crewId} on {date}
          </Typography>
          <Button variant="outlined" size="small" onClick={fetchRoute}>Retry</Button>
        </Box>
      </Paper>
    )
  }

  return (
    <Paper variant="outlined" className={classes.root}>
      <div className={classes.header}>
        <DirectionsIcon style={{ color: crewColor }} />
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {crewName || crewId} — {date}
        </Typography>
        <Button variant="outlined" size="small" onClick={fetchRoute}>Refresh</Button>
      </div>

      <div className={classes.chipRow}>
        <Chip size="small" label={`${stops.length} stops`} />
        <Chip size="small" label={`Service: ${totalServiceMin} min`} />
        {totalDriveMin > 0 && <Chip size="small" label={`Drive: ${Math.round(totalDriveMin)} min`} />}
        {totalMiles > 0 && <Chip size="small" label={`${Math.round(totalMiles * 10) / 10} mi`} />}
        <Chip size="small" variant="outlined" label={`Mode: ${meta.mode || 'unknown'}`} />
        {meta.missingCoordinates > 0 && (
          <Chip size="small" color="secondary" label={`${meta.missingCoordinates} missing coords`} />
        )}
      </div>

      {/* Route map */}
      {process.env.REACT_APP_GMAPKEY && polylinePath.length >= 2 && (
        <div className={classes.mapContainer}>
          <LoadScript googleMapsApiKey={process.env.REACT_APP_GMAPKEY}>
            <GoogleMap
              mapContainerStyle={ROUTE_MAP_STYLE}
              center={DEFAULT_CENTER}
              zoom={11}
              onLoad={setMapRef}
              options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
            >
              <Polyline
                path={polylinePath}
                options={{ strokeColor: crewColor, strokeOpacity: 0.8, strokeWeight: 4 }}
              />
              {stops.map(stop => (
                <Marker
                  key={stop.aspirePropertyId}
                  position={{ lat: stop.latitude, lng: stop.longitude }}
                  label={{
                    text: String(stop.order),
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '11px'
                  }}
                  icon={{
                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                    scale: 14,
                    fillColor: crewColor,
                    fillOpacity: 0.9,
                    strokeColor: '#ffffff',
                    strokeWeight: 2
                  }}
                  title={stop.propertyName}
                />
              ))}
            </GoogleMap>
          </LoadScript>
        </div>
      )}

      {/* Stop list table */}
      <div style={{ overflowX: 'auto' }}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell} style={{ width: 40 }}>#</TableCell>
              <TableCell className={classes.headerCell}>Property</TableCell>
              <TableCell className={classes.headerCell} style={{ width: 80 }}>Service</TableCell>
              <TableCell className={classes.headerCell} style={{ width: 80 }}>Drive</TableCell>
              <TableCell className={classes.headerCell} style={{ width: 70 }}>Miles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stops.map(stop => (
              <TableRow key={stop.aspirePropertyId}>
                <TableCell className={classes.cell}>
                  <span className={classes.orderBadge} style={{ backgroundColor: crewColor }}>
                    {stop.order}
                  </span>
                </TableCell>
                <TableCell className={classes.cell}>
                  <Typography variant="body2">{stop.propertyName || '-'}</Typography>
                  <Typography className={classes.addressText}>
                    {stop.address || ''} {'\u00B7'} #{stop.aspirePropertyId}
                  </Typography>
                </TableCell>
                <TableCell className={classes.cell}>{stop.serviceMinutes || 0} min</TableCell>
                <TableCell className={classes.cell}>
                  {stop.driveTimeMinutes != null ? `${Math.round(stop.driveTimeMinutes)} min` : '-'}
                </TableCell>
                <TableCell className={classes.cell}>
                  {stop.driveMiles != null ? `${Math.round(stop.driveMiles * 10) / 10}` : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Paper>
  )
}
